import { Component, h, Prop, State, Element, Method } from '@stencil/core';

export interface ProjectImage {
  src: string;
  alt?: string;
}

@Component({
  tag: 'hd-project-card',
  styleUrl: './hd-project-card.pcss',
  shadow: true,
})
export class HdProjectCard {
  @Element() el: HTMLElement;

  /** Main title for the project card */
  @Prop() cardTitle: string = '';

  /** Link for the project (title and images will link to this) */
  @Prop() link: string = '';

  /** Featured image object or URL string */
  @Prop() featuredImage: string | ProjectImage = '';

  /** Alt text for the featured image */
  @Prop() featuredImageAlt: string = '';

  /** Additional images array */
  @Prop() additionalImages: ProjectImage[] = [];

  /** Additional images as JSON string (alternative to additionalImages property) */
  @Prop() additionalImagesJson: string = '';

  /** Description text below the image (category, location, etc.) */
  @Prop() description: string = '';

  /** Whether to use native scroll controls (Chrome supports ::scroll-button and ::scroll-marker) */
  @Prop() useNativeControls: boolean = true;

  @State() currentSlide: number = 0;
  @State() supportsNativeScrollControls: boolean = false;

  private scrollContainer: HTMLElement;
  private images: ProjectImage[] = [];
  private parsedAdditionalImages: ProjectImage[] = [];

  componentWillLoad() {
    // Parse additional images from JSON string if provided
    this.parseAdditionalImagesJson();

    // Combine featured image and additional images
    this.prepareImages();

    // Detect native scroll controls support (Chrome 95+)
    this.detectNativeScrollSupport();
  }

  private parseAdditionalImagesJson() {
    if (this.additionalImagesJson) {
      try {
        this.parsedAdditionalImages = JSON.parse(this.additionalImagesJson);
      } catch (e) {
        console.error('[HADID] Error parsing additional images JSON:', e);
        this.parsedAdditionalImages = [];
      }
    } else {
      this.parsedAdditionalImages = [];
    }
  }

  private prepareImages() {
    this.images = [];

    // Add featured image
    if (this.featuredImage) {
      if (typeof this.featuredImage === 'string') {
        this.images.push({
          src: this.featuredImage,
          alt: this.featuredImageAlt || this.cardTitle || 'Featured image'
        });
      } else {
        this.images.push(this.featuredImage);
      }
    }

    // Add additional images (prioritize parsed JSON over property)
    const additionalImagesToUse = this.parsedAdditionalImages.length > 0
      ? this.parsedAdditionalImages
      : this.additionalImages;

    console.log('additionalImages', additionalImagesToUse);
    if (additionalImagesToUse && additionalImagesToUse.length > 0) {
      this.images.push(...additionalImagesToUse);
    }
  }

  private detectNativeScrollSupport() {
    // Check if browser supports native scroll controls
    // This is primarily supported in Chrome 95+ for ::scroll-button and ::scroll-marker
    const userAgent = navigator.userAgent;
    const isChrome = /Chrome/.test(userAgent) && !/Edg/.test(userAgent);
    const chromeVersion = isChrome ? parseInt(userAgent.match(/Chrome\/(\d+)/)?.[1] || '0') : 0;

    this.supportsNativeScrollControls = this.useNativeControls && isChrome && chromeVersion >= 95;
  }

  @Method()
  async goToSlide(index: number) {
    if (index < 0 || index >= this.images.length) return;

    this.currentSlide = index;
    if (this.scrollContainer) {
      const slideWidth = this.scrollContainer.offsetWidth;
      this.scrollContainer.scrollTo({
        left: slideWidth * index,
        behavior: 'smooth'
      });
    }
  }

  @Method()
  async nextSlide() {
    const nextIndex = (this.currentSlide + 1) % this.images.length;
    await this.goToSlide(nextIndex);
  }

  @Method()
  async prevSlide() {
    const prevIndex = this.currentSlide === 0 ? this.images.length - 1 : this.currentSlide - 1;
    await this.goToSlide(prevIndex);
  }

  private handleScroll = () => {
    if (!this.scrollContainer) return;

    const slideWidth = this.scrollContainer.offsetWidth;
    const scrollLeft = this.scrollContainer.scrollLeft;
    const newSlide = Math.round(scrollLeft / slideWidth);

    if (newSlide !== this.currentSlide) {
      this.currentSlide = newSlide;
    }
  };

  private handleImageClick = (event: Event) => {
    if (this.link) {
      event.preventDefault();
      window.open(this.link, '_self');
    }
  };

  private renderTitle() {
    if (!this.cardTitle) return null;

    return (
      <h2 class="project-title">{this.cardTitle}</h2>
    );
  }

  private renderDescription() {
    if (!this.description) return null;

    return (
      <p class="project-description">{this.description}</p>
    );
  }

  private renderNativeCarousel() {
    return (
      <div class="carousel-container native">
        <button
          class="carousel-arrow carousel-arrow-left"
          onClick={() => this.prevSlide()}
          disabled={this.images.length <= 1}
          aria-label="Previous image"
        >
          <hd-icon svg="leftChevron" width="13px" height="27px"></hd-icon>
        </button>

        <div
          class="carousel-scroll-container native-scroll"
          ref={el => this.scrollContainer = el}
          onScroll={this.handleScroll}
        >
          {this.images.map((image, index) => (
            <div key={index} class="carousel-slide">
              <div
                class="image-wrapper"
                onClick={this.handleImageClick}
                style={{ cursor: this.link ? 'pointer' : 'default' }}
              >
                <img
                  src={image.src}
                  alt={image.alt || `Slide ${index + 1}`}
                  loading={index === 0 ? 'eager' : 'lazy'}
                />
              </div>
            </div>
          ))}
        </div>

        <button
          class="carousel-arrow carousel-arrow-right"
          onClick={() => this.nextSlide()}
          disabled={this.images.length <= 1}
          aria-label="Next image"
        >
          <hd-icon svg="rightChevron" width="13px" height="27px"></hd-icon>
        </button>

        {this.images.length > 1 && (
          <div class="carousel-dots">
            {this.images.map((_, index) => (
              <button
                key={index}
                class={{
                  'carousel-dot': true,
                  'active': index === this.currentSlide
                }}
                onClick={() => this.goToSlide(index)}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  private renderFallbackCarousel() {
    return (
      <div class="carousel-container fallback">
        <button
          class="carousel-arrow carousel-arrow-left"
          onClick={() => this.prevSlide()}
          disabled={this.images.length <= 1}
          aria-label="Previous image"
        >
          <svg class="svg-icon" viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
            <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
          </svg>
        </button>

        <div
          class="carousel-scroll-container"
          ref={el => this.scrollContainer = el}
          onScroll={this.handleScroll}
        >
          {this.images.map((image, index) => (
            <div key={index} class="carousel-slide">
              <div
                class="image-wrapper"
                onClick={this.handleImageClick}
                style={{ cursor: this.link ? 'pointer' : 'default' }}
              >
                <img
                  src={image.src}
                  alt={image.alt || `Slide ${index + 1}`}
                  loading={index === 0 ? 'eager' : 'lazy'}
                />
              </div>
            </div>
          ))}
        </div>

        <button
          class="carousel-arrow carousel-arrow-right"
          onClick={() => this.nextSlide()}
          disabled={this.images.length <= 1}
          aria-label="Next image"
        >
          <svg class="svg-icon" viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
            <path d="M8.59 16.59L10 18l6-6-6-6-1.41 1.41L13.17 12z"/>
          </svg>
        </button>

        {this.images.length > 1 && (
          <div class="carousel-dots">
            {this.images.map((_, index) => (
              <button
                key={index}
                class={{
                  'carousel-dot': true,
                  'active': index === this.currentSlide
                }}
                onClick={() => this.goToSlide(index)}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  render() {
    if (this.images.length === 0) {
      return (
        <div class="project-card empty">
          {this.renderTitle()}
          {this.renderDescription()}
          <div class="no-images">No images available</div>
        </div>
      );
    }

    if (this.images.length === 1) {
      return (
        <div class="project-card single">
          <div class="single-image">
            <div
              class="image-wrapper"
              onClick={this.handleImageClick}
              style={{ cursor: this.link ? 'pointer' : 'default' }}
            >
              <img
                src={this.images[0].src}
                alt={this.images[0].alt || this.cardTitle || 'Project image'}
              />
            </div>
          </div>
          {this.renderTitle()}
          {this.renderDescription()}
        </div>
      );
    }

    return (
      <div class="project-card">
        {this.supportsNativeScrollControls ? this.renderNativeCarousel() : this.renderFallbackCarousel()}
        <a href={this.link} rel="noopener noreferrer" class="project-card-details">
          {this.renderDescription()}
          {this.renderTitle()}
        </a>
      </div>
    );
  }
}